import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Shield, Settings, Activity, Plus, Edit, Trash2, X, Check, ChevronDown, ChevronUp, Loader2, AlertTriangle, GripVertical, ToggleLeft, ToggleRight } from 'lucide-react';
import { cn, API_BASE_URL } from '../lib/utils';
import { useAppSelector } from '../store';
import { useSystemConfig, type ConfigListItem, type RoleConfig } from '../contexts/SystemConfigContext';
import type { User, UserRole } from '../types';

const availableColorOptions = [
  { label: 'Red', value: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { label: 'Orange', value: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { label: 'Blue', value: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { label: 'Green', value: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { label: 'Purple', value: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  { label: 'Cyan', value: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  { label: 'Yellow', value: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  { label: 'Pink', value: 'bg-pink-500/20 text-pink-400 border-pink-500/30' },
];

type ConfigListKey = 'eventCategories' | 'incidentCategories' | 'incidentSeverities' | 'incidentStatuses' | 'eventStatuses' | 'resourceTypes' | 'skillLevels' | 'departments';

const editableListMeta: { key: ConfigListKey; label: string; description: string }[] = [
  { key: 'eventCategories', label: 'Event Categories', description: 'Categories for main event types (e.g. Festival, Sporting, Emergency)' },
  { key: 'incidentCategories', label: 'Incident Categories', description: 'Categories for classifying incidents (e.g. Fire, Medical, Criminal)' },
  { key: 'incidentSeverities', label: 'Incident Severities', description: 'Severity levels for incidents (e.g. Critical, High, Medium)' },
  { key: 'incidentStatuses', label: 'Incident Statuses', description: 'Workflow statuses for incidents (e.g. Dispatched, On Scene, Resolved)' },
  { key: 'eventStatuses', label: 'Event Statuses', description: 'Lifecycle statuses for events (e.g. Open, Supervision, Closed)' },
  { key: 'resourceTypes', label: 'Resource Types', description: 'Types of resources that can be assigned to events (e.g. Vehicle, Personnel)' },
  { key: 'skillLevels', label: 'Skill Levels', description: 'Qualification levels for personnel (e.g. FREC-3, FREC-4, EMT, Paramedic)' },
  { key: 'departments', label: 'Departments', description: 'Organisational departments for users and resources' },
];

type AdminTab = 'users' | 'roles' | 'system' | 'logs';

// Define which tabs belong to each section
const sectionTabs: Record<string, AdminTab[]> = {
  users: ['users'],
  roles: ['roles', 'system'],
  system: ['roles', 'system'],
  logs: ['logs'],
};

export default function AdminPage() {
  const [searchParams] = useSearchParams();
  const { token } = useAppSelector((state) => state.auth);

  const initialTab = (searchParams.get('tab') as AdminTab) || 'users';
  const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);

  // Determine which tabs to show based on the initial tab
  const visibleTabs = sectionTabs[initialTab] || sectionTabs.users;

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchUsers, setSearchUsers] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [userError, setUserError] = useState('');

  // Form state for add/edit user
  const [formFirstName, setFormFirstName] = useState('');
  const [formSurname, setFormSurname] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formUsername, setFormUsername] = useState('');
  const [formRole, setFormRole] = useState<UserRole>('mdt_officer');
  const [formDepartment, setFormDepartment] = useState('');
  const [formSkillLevel, setFormSkillLevel] = useState('');
  const [formIdNumber, setFormIdNumber] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formConfirmPassword, setFormConfirmPassword] = useState('');

  // System config from context
  const { config, updateConfig } = useSystemConfig();
  const roles = config?.roles || [];
  const allPermissions = config?.permissions || [];

  // Roles UI state
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleColor, setNewRoleColor] = useState(availableColorOptions[4].value);

  // System config lists editing
  const [editingListKey, setEditingListKey] = useState<ConfigListKey | null>(null);
  const [newItemLabel, setNewItemLabel] = useState('');

  // Audit logs state
  type AuditLog = {
    id: string;
    timestamp: string;
    userId: string;
    userName: string;
    userRole?: string | null;
    action: string;
    category: string;
    eventId?: string | null;
    incidentId?: string | null;
    resourceId?: string | null;
    details: string;
    metadata?: Record<string, unknown> | null;
    ipAddress?: string | null;
    userAgent?: string | null;
  };
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditScope, setAuditScope] = useState<'all' | 'event' | 'incident'>('all');
  const [auditSelectedEventId, setAuditSelectedEventId] = useState<string>('');
  const [auditSelectedIncidentId, setAuditSelectedIncidentId] = useState<string>('');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditCategory, setAuditCategory] = useState<string>('');
  const [auditFrom, setAuditFrom] = useState('');
  const [auditTo, setAuditTo] = useState('');
  const [scopeSearch, setScopeSearch] = useState('');
  const [auditEvents, setAuditEvents] = useState<Array<{ id: string; name: string }>>([]);
  const [auditIncidents, setAuditIncidents] = useState<Array<{ id: string; title: string; eventId: string }>>([]);
  const [expandedAuditId, setExpandedAuditId] = useState<string | null>(null);

  const tabs: { id: AdminTab; label: string; icon: React.ElementType }[] = [
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'roles', label: 'Roles & Permissions', icon: Shield },
    { id: 'system', label: 'System Config', icon: Settings },
    { id: 'logs', label: 'Audit Logs', icon: Activity },
  ];

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch { /* ignore */ } finally {
      setLoadingUsers(false);
    }
  }, [token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Audit Logs ──────────────────────────────────────────────
  const fetchAuditLogs = useCallback(async () => {
    if (!token) return;
    setAuditLoading(true);
    setAuditError('');
    try {
      const params = new URLSearchParams();
      if (auditScope === 'event' && auditSelectedEventId) params.set('eventId', auditSelectedEventId);
      if (auditScope === 'incident' && auditSelectedIncidentId) params.set('incidentId', auditSelectedIncidentId);
      if (auditCategory) params.set('category', auditCategory);
      if (auditSearch.trim()) params.set('search', auditSearch.trim());
      if (auditFrom) params.set('from', new Date(auditFrom).toISOString());
      if (auditTo) params.set('to', new Date(auditTo).toISOString());
      const res = await fetch(`${API_BASE_URL}/audit?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setAuditError(err.error || `Failed to load audit logs (${res.status})`);
        setAuditLogs([]);
        setAuditTotal(0);
        return;
      }
      const data = await res.json();
      setAuditLogs(data.logs || []);
      setAuditTotal(data.total || 0);
    } catch {
      setAuditError('Network error loading audit logs');
    } finally {
      setAuditLoading(false);
    }
  }, [token, auditScope, auditSelectedEventId, auditSelectedIncidentId, auditCategory, auditSearch, auditFrom, auditTo]);

  // Load events + incidents (for filter dropdowns) when on the logs tab
  useEffect(() => {
    if (activeTab !== 'logs' || !token) return;
    (async () => {
      try {
        const [evRes, incRes] = await Promise.all([
          fetch(`${API_BASE_URL}/events`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE_URL}/incidents`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (evRes.ok) {
          const d = await evRes.json();
          setAuditEvents((d.events || []).map((e: { id: string; name: string }) => ({ id: e.id, name: e.name })));
        }
        if (incRes.ok) {
          const d = await incRes.json();
          setAuditIncidents((d.incidents || []).map((i: { id: string; title: string; eventId: string }) => ({ id: i.id, title: i.title, eventId: i.eventId })));
        }
      } catch { /* ignore */ }
    })();
  }, [activeTab, token]);

  // Auto-fetch audit logs when filters change (and tab is active)
  useEffect(() => {
    if (activeTab !== 'logs') return;
    const t = setTimeout(fetchAuditLogs, 300);
    return () => clearTimeout(t);
  }, [activeTab, fetchAuditLogs]);

  const resetForm = () => {
    setFormFirstName('');
    setFormSurname('');
    setFormEmail('');
    setFormUsername('');
    setFormRole('mdt_officer');
    setFormDepartment('');
    setFormSkillLevel('');
    setFormIdNumber('');
    setFormPassword('');
    setFormConfirmPassword('');
    setUserError('');
  };

  const openEditUser = (user: User) => {
    setEditingUser(user);
    setFormFirstName(user.firstName);
    setFormSurname(user.lastName);
    setFormEmail(user.email);
    setFormUsername(user.username || user.email.split('@')[0]);
    setFormRole(user.role);
    setFormDepartment(user.department);
    setFormSkillLevel('');
    setFormIdNumber('');
    setFormPassword('');
    setFormConfirmPassword('');
    setUserError('');
    setShowAddUser(false);
  };

  const openAddUser = () => {
    setEditingUser(null);
    resetForm();
    setShowAddUser(true);
  };

  const handleSaveUser = async () => {
    if (!formFirstName.trim() || !formSurname.trim() || !formEmail.trim() || !formUsername.trim()) {
      setUserError('First name, surname, email, and username are required.');
      return;
    }
    if (!editingUser && !formPassword.trim()) {
      setUserError('Password is required for new users.');
      return;
    }
    if (formPassword && formPassword !== formConfirmPassword) {
      setUserError('Passwords do not match.');
      return;
    }
    setSaving(true);
    setUserError('');
    try {
      const body: Record<string, string> = {
        firstName: formFirstName.trim(),
        lastName: formSurname.trim(),
        email: formEmail.trim(),
        username: formUsername.trim(),
        role: formRole,
        department: formDepartment.trim(),
        skillLevel: formSkillLevel.trim(),
        idNumber: formIdNumber.trim(),
      };
      if (formPassword) body.password = formPassword;

      if (editingUser) {
        // PATCH
        const res = await fetch(`${API_BASE_URL}/users/${editingUser.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json();
          setUserError(err.error || 'Failed to update user');
          return;
        }
      } else {
        // POST
        if (!formPassword) body.password = 'changeme123';
        const res = await fetch(`${API_BASE_URL}/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = await res.json();
          setUserError(err.error || 'Failed to create user');
          return;
        }
      }
      resetForm();
      setEditingUser(null);
      setShowAddUser(false);
      fetchUsers();
    } catch {
      setUserError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        setUserError(err.error || 'Failed to delete user');
      }
      setDeletingUserId(null);
      fetchUsers();
    } catch { setUserError('Network error'); } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    `${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(searchUsers.toLowerCase())
  );

  const getRoleColor = (roleId: string) => {
    const role = roles.find((r) => r.id === roleId);
    return role?.colorClass || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const roleOptions = roles.map((r) => ({ value: r.id as UserRole, label: r.name }));

  const togglePermission = (roleId: string, permission: string) => {
    const updated = roles.map((r) => {
      if (r.id !== roleId) return r;
      const has = r.permissions.includes(permission);
      return { ...r, permissions: has ? r.permissions.filter((p) => p !== permission) : [...r.permissions, permission] };
    });
    updateConfig({ roles: updated });
  };

  const handleAddRole = () => {
    if (!newRoleName.trim()) return;
    const id = newRoleName.trim().toLowerCase().replace(/\s+/g, '_');
    if (roles.find((r) => r.id === id)) return;
    const newRole: RoleConfig = { id, name: newRoleName.trim(), colorClass: newRoleColor, permissions: ['view_dashboard', 'view_map'] };
    updateConfig({ roles: [...roles, newRole] });
    setNewRoleName('');
    setNewRoleColor(availableColorOptions[4].value);
    setShowAddRole(false);
    setEditingRoleId(id);
    setExpandedRoleId(id);
  };

  const handleDeleteRole = (roleId: string) => {
    const builtIn = ['command_admin', 'command_supervisor', 'command_dispatcher', 'mdt_supervisor', 'mdt_officer'];
    if (builtIn.includes(roleId)) return;
    updateConfig({ roles: roles.filter((r) => r.id !== roleId) });
    if (editingRoleId === roleId) setEditingRoleId(null);
    if (expandedRoleId === roleId) setExpandedRoleId(null);
  };

  // System config list helpers
  const getListItems = (key: ConfigListKey): ConfigListItem[] => {
    return (config?.[key] as ConfigListItem[]) || [];
  };

  const handleToggleListItem = (key: ConfigListKey, itemId: string) => {
    const items = getListItems(key).map((item) =>
      item.id === itemId ? { ...item, enabled: !item.enabled } : item
    );
    updateConfig({ [key]: items });
  };

  const handleAddListItem = (key: ConfigListKey) => {
    if (!newItemLabel.trim()) return;
    const id = newItemLabel.trim().toLowerCase().replace(/\s+/g, '_');
    const items = getListItems(key);
    if (items.find((i) => i.id === id)) return;
    updateConfig({ [key]: [...items, { id, label: newItemLabel.trim(), enabled: true }] });
    setNewItemLabel('');
  };

  const handleDeleteListItem = (key: ConfigListKey, itemId: string) => {
    const items = getListItems(key).filter((i) => i.id !== itemId);
    updateConfig({ [key]: items });
  };

  const handleRenameListItem = (key: ConfigListKey, itemId: string, newLabel: string) => {
    const items = getListItems(key).map((item) =>
      item.id === itemId ? { ...item, label: newLabel } : item
    );
    updateConfig({ [key]: items });
  };

  return (
    <div className="h-full flex flex-col gap-3">
      {/* Header + Tabs - fixed */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-lg font-bold text-foreground">Administration</h1>
          <p className="text-xs text-muted-foreground">System configuration</p>
        </div>
        <div className="flex gap-1 p-0.5 bg-card rounded-lg border border-border">
          {tabs.filter(tab => visibleTabs.includes(tab.id)).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 overflow-y-auto">

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Search + Add */}
          <div className="flex items-center justify-between">
            <input
              type="text"
              value={searchUsers}
              onChange={(e) => setSearchUsers(e.target.value)}
              placeholder="Search users..."
              className="px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 w-64"
            />
            <button
              onClick={openAddUser}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add User
            </button>
          </div>

          {/* Add / Edit User Form */}
          {(showAddUser || editingUser) && (
            <div className="glass-panel p-5 border-2 border-primary/30 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  {editingUser ? <Edit className="w-4 h-4 text-primary" /> : <Plus className="w-4 h-4 text-primary" />}
                  {editingUser ? `Edit User: ${editingUser.firstName} ${editingUser.lastName}` : 'Create New User'}
                </h3>
                <button
                  onClick={() => { setShowAddUser(false); setEditingUser(null); resetForm(); }}
                  className="p-1 hover:bg-accent rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {userError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/30">
                  <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
                  <span className="text-xs text-destructive">{userError}</span>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">First Name *</label>
                  <input
                    value={formFirstName}
                    onChange={(e) => setFormFirstName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Surname *</label>
                  <input
                    value={formSurname}
                    onChange={(e) => setFormSurname(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Surname"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Email *</label>
                  <input
                    type="email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="user@c2system.com"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Username *</label>
                  <input
                    value={formUsername}
                    onChange={(e) => setFormUsername(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="Used for login"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Department</label>
                  <input
                    value={formDepartment}
                    onChange={(e) => setFormDepartment(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="e.g. Field Ops"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">ID Number</label>
                  <input
                    value={formIdNumber}
                    onChange={(e) => setFormIdNumber(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder="e.g. EMP-12345"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Role</label>
                  <select
                    value={formRole}
                    onChange={(e) => setFormRole(e.target.value as UserRole)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    {roleOptions.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Skill Level</label>
                  <select
                    value={formSkillLevel}
                    onChange={(e) => setFormSkillLevel(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  >
                    <option value="">Select skill level</option>
                    <option value="FREC-3">FREC-3</option>
                    <option value="FREC-4">FREC-4</option>
                    <option value="EMT">EMT</option>
                    <option value="Paramedic">Paramedic</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">{editingUser ? 'New Password (optional)' : 'Password *'}</label>
                  <input
                    type="password"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder={editingUser ? 'Leave blank to keep' : 'Enter password'}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">{editingUser ? 'Confirm New Password' : 'Confirm Password *'}</label>
                  <input
                    type="password"
                    value={formConfirmPassword}
                    onChange={(e) => setFormConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder={editingUser ? 'Confirm if changing' : 'Confirm password'}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={handleSaveUser}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  {editingUser ? 'Save Changes' : 'Create User'}
                </button>
                <button
                  onClick={() => { setShowAddUser(false); setEditingUser(null); resetForm(); }}
                  className="px-4 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Delete Confirmation */}
          {deletingUserId && (
            <div className="glass-panel p-4 border-2 border-destructive/30 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-destructive" />
                <span className="text-sm text-foreground">Are you sure you want to delete this user? This cannot be undone.</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDeleteUser(deletingUserId)}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setDeletingUserId(null)}
                  className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* User Table */}
          {loadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="glass-panel overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-4">User</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-4">Role</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-4">Department</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-4">Badge</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider p-4">Status</th>
                    <th className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className={cn('hover:bg-accent/50 transition-colors', editingUser?.id === u.id && 'bg-primary/5')}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                              {u.firstName[0]}{u.lastName[0]}
                            </div>
                            <div className={cn('absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card', u.isOnline ? 'bg-green-400' : 'bg-gray-500')} />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{u.firstName} {u.lastName}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={cn('text-xs px-2 py-1 rounded-full border font-medium', getRoleColor(u.role))}>
                          {u.role.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-foreground">{u.department}</td>
                      <td className="p-4 text-sm text-foreground font-mono">{u.badgeNumber}</td>
                      <td className="p-4">
                        <span className={cn('text-xs font-medium', u.isOnline ? 'text-green-400' : 'text-gray-500')}>
                          {u.isOnline ? 'Online' : 'Offline'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditUser(u)}
                            className="p-1.5 rounded-lg hover:bg-accent transition-colors"
                            title="Edit user"
                          >
                            <Edit className="w-3.5 h-3.5 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => setDeletingUserId(u.id)}
                            className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors"
                            title="Delete user"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredUsers.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">
                        {searchUsers ? 'No users match your search.' : 'No users found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{roles.length} roles configured · {allPermissions.length} permissions available</p>
            <button
              onClick={() => setShowAddRole(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add New Role
            </button>
          </div>

          {showAddRole && (
            <div className="glass-panel p-4 border-2 border-primary/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Plus className="w-4 h-4 text-primary" /> Create New Role
                </h3>
                <button onClick={() => setShowAddRole(false)} className="p-1 hover:bg-accent rounded-lg transition-colors">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground block mb-1">Role Name</label>
                  <input value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} placeholder="e.g. analyst, pilot, medic" className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
                </div>
                <div className="w-40">
                  <label className="text-xs text-muted-foreground block mb-1">Color</label>
                  <select value={newRoleColor} onChange={(e) => setNewRoleColor(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                    {availableColorOptions.map((c) => (<option key={c.label} value={c.value}>{c.label}</option>))}
                  </select>
                </div>
                <button onClick={handleAddRole} disabled={!newRoleName.trim()} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
                  <Check className="w-4 h-4" /> Create
                </button>
              </div>
            </div>
          )}

          {/* Role Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {roles.map((role) => {
              const isEditing = editingRoleId === role.id;
              const isExpanded = expandedRoleId === role.id;
              const builtIn = ['command_admin', 'command_supervisor', 'command_dispatcher', 'mdt_supervisor', 'mdt_officer'];
              const isBuiltIn = builtIn.includes(role.id);
              const grantedPerms = allPermissions.filter((p) => role.permissions.includes(p.id));
              const deniedPerms = allPermissions.filter((p) => !role.permissions.includes(p.id));
              const permCategories = [...new Set(allPermissions.map((p) => p.category || 'General'))];

              return (
                <div key={role.id} className={cn('glass-panel p-4 transition-all', isEditing && 'border-primary/40 ring-1 ring-primary/20')}>
                  <div className="flex items-center justify-between mb-3">
                    <span className={cn('text-sm px-3 py-1 rounded-full border font-semibold', role.colorClass)}>
                      {role.name.toUpperCase()}
                    </span>
                    <div className="flex items-center gap-1">
                      {!isBuiltIn && (
                        <button onClick={() => handleDeleteRole(role.id)} className="p-1 rounded hover:bg-destructive/10 transition-colors" title="Delete role">
                          <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (isEditing) { setEditingRoleId(null); setExpandedRoleId(null); }
                          else { setEditingRoleId(role.id); setExpandedRoleId(role.id); }
                        }}
                        className={cn('text-xs font-medium px-2 py-1 rounded transition-colors', isEditing ? 'bg-primary text-primary-foreground' : 'text-primary hover:bg-primary/10')}
                      >
                        {isEditing ? 'Done' : 'Edit Permissions'}
                      </button>
                    </div>
                  </div>

                  {/* View mode */}
                  {!isEditing && (
                    <div className="space-y-1.5 text-sm">
                      {grantedPerms.slice(0, isExpanded ? undefined : 5).map((p) => (
                        <div key={p.id} className="flex items-center gap-2 text-muted-foreground">
                          <span className="text-green-400 text-xs">+</span>
                          <span className="text-xs">{p.label}</span>
                        </div>
                      ))}
                      {deniedPerms.slice(0, isExpanded ? undefined : 2).map((p) => (
                        <div key={p.id} className="flex items-center gap-2 text-muted-foreground/50">
                          <span className="text-red-400 text-xs">-</span>
                          <span className="text-xs">{p.label}</span>
                        </div>
                      ))}
                      {!isExpanded && grantedPerms.length > 5 && (
                        <button onClick={() => setExpandedRoleId(role.id)} className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 mt-1">
                          <ChevronDown className="w-3 h-3" /> Show all ({grantedPerms.length} granted, {deniedPerms.length} denied)
                        </button>
                      )}
                      {isExpanded && (
                        <button onClick={() => setExpandedRoleId(null)} className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 mt-1">
                          <ChevronUp className="w-3 h-3" /> Collapse
                        </button>
                      )}
                    </div>
                  )}

                  {/* Edit mode - grouped by category */}
                  {isEditing && (
                    <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                      {permCategories.map((cat) => {
                        const catPerms = allPermissions.filter((p) => (p.category || 'General') === cat);
                        return (
                          <div key={cat}>
                            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1 px-1">{cat}</p>
                            <div className="space-y-0.5">
                              {catPerms.map((perm) => {
                                const isGranted = role.permissions.includes(perm.id);
                                return (
                                  <label key={perm.id} className={cn('flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors text-xs', isGranted ? 'bg-green-500/5 hover:bg-green-500/10' : 'hover:bg-accent/50')}>
                                    <input type="checkbox" checked={isGranted} onChange={() => togglePermission(role.id, perm.id)} className="w-3.5 h-3.5 accent-primary rounded" />
                                    <span className={cn(isGranted ? 'text-foreground' : 'text-muted-foreground')}>{perm.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* System Config Tab */}
      {activeTab === 'system' && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">Customise the dropdown lists and options used throughout the system. Add, rename, disable, or remove items. Changes take effect immediately.</p>

          {editableListMeta.map((meta) => {
            const items = getListItems(meta.key);
            const isExpanded = editingListKey === meta.key;

            return (
              <div key={meta.key} className="glass-panel overflow-hidden">
                <button
                  onClick={() => setEditingListKey(isExpanded ? null : meta.key)}
                  className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-muted-foreground/40" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-foreground">{meta.label}</p>
                      <p className="text-xs text-muted-foreground">{meta.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{items.filter((i) => i.enabled).length}/{items.length} active</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-border p-4 space-y-3">
                    {/* Add new item */}
                    <div className="flex gap-2">
                      <input
                        value={newItemLabel}
                        onChange={(e) => setNewItemLabel(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddListItem(meta.key)}
                        placeholder={`Add new ${meta.label.toLowerCase().replace(/s$/, '')}...`}
                        className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <button
                        onClick={() => handleAddListItem(meta.key)}
                        disabled={!newItemLabel.trim()}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add
                      </button>
                    </div>

                    {/* List items */}
                    <div className="space-y-1">
                      {items.map((item) => (
                        <div key={item.id} className={cn('flex items-center gap-2 px-3 py-2 rounded-lg border transition-all', item.enabled ? 'border-border bg-card' : 'border-border/50 bg-card/50 opacity-60')}>
                          <button
                            onClick={() => handleToggleListItem(meta.key, item.id)}
                            className="flex-shrink-0"
                            title={item.enabled ? 'Disable' : 'Enable'}
                          >
                            {item.enabled
                              ? <ToggleRight className="w-5 h-5 text-primary" />
                              : <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                            }
                          </button>
                          <input
                            defaultValue={item.label}
                            onBlur={(e) => {
                              if (e.target.value.trim() && e.target.value.trim() !== item.label) {
                                handleRenameListItem(meta.key, item.id, e.target.value.trim());
                              }
                            }}
                            className="flex-1 bg-transparent text-sm text-foreground focus:outline-none focus:bg-background focus:px-2 focus:py-0.5 focus:rounded focus:ring-1 focus:ring-primary/30 transition-all"
                          />
                          <span className="text-[10px] text-muted-foreground font-mono">{item.id}</span>
                          <button
                            onClick={() => handleDeleteListItem(meta.key, item.id)}
                            className="p-1 rounded hover:bg-destructive/10 transition-colors flex-shrink-0"
                            title="Remove item"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                          </button>
                        </div>
                      ))}
                      {items.length === 0 && (
                        <p className="text-center text-xs text-muted-foreground py-4">No items. Add one above.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Audit Logs Tab */}
      {activeTab === 'logs' && (() => {
        const categoryColors: Record<string, string> = {
          auth: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
          user: 'bg-pink-500/15 text-pink-400 border-pink-500/30',
          event: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
          incident: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
          dispatch: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
          mdt: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
          resource: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30',
          prf: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
          config: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
          audit: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
          system: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
        };

        const filteredEvents = auditEvents.filter(e =>
          !scopeSearch || e.name.toLowerCase().includes(scopeSearch.toLowerCase()) || e.id.toLowerCase().includes(scopeSearch.toLowerCase())
        );
        const filteredIncidents = auditIncidents.filter(i =>
          !scopeSearch || i.title.toLowerCase().includes(scopeSearch.toLowerCase()) || i.id.toLowerCase().includes(scopeSearch.toLowerCase())
        );

        const exportCSV = () => {
          const header = ['Timestamp', 'User', 'Role', 'Category', 'Action', 'Event', 'Incident', 'Resource', 'IP', 'Details'];
          const rows = auditLogs.map(l => [
            l.timestamp,
            l.userName,
            l.userRole || '',
            l.category,
            l.action,
            l.eventId || '',
            l.incidentId || '',
            l.resourceId || '',
            l.ipAddress || '',
            (l.details || '').replace(/"/g, '""'),
          ]);
          const csv = [header, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        };

        return (
          <div className="space-y-3">
            {/* Filter bar */}
            <div className="glass-panel p-3 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">Audit Log Filters</h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{auditTotal} entries</span>
                  <button onClick={exportCSV} disabled={auditLogs.length === 0} className="px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors disabled:opacity-50">
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Scope buttons */}
              <div className="flex gap-2">
                {(['all', 'event', 'incident'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => { setAuditScope(s); setAuditSelectedEventId(''); setAuditSelectedIncidentId(''); setScopeSearch(''); }}
                    className={cn(
                      'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                      auditScope === s ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {s === 'all' ? 'All Logs' : s === 'event' ? 'By Event' : 'By Incident'}
                  </button>
                ))}
              </div>

              {/* Scope-specific selectors */}
              {auditScope === 'event' && (
                <div className="space-y-2">
                  <input
                    value={scopeSearch}
                    onChange={(e) => setScopeSearch(e.target.value)}
                    placeholder="Search events by name or ID..."
                    className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-border bg-card divide-y divide-border">
                    {filteredEvents.length === 0 && <p className="p-2 text-xs text-muted-foreground text-center">No events match.</p>}
                    {filteredEvents.map(e => (
                      <button
                        key={e.id}
                        onClick={() => setAuditSelectedEventId(e.id)}
                        className={cn('w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex justify-between',
                          auditSelectedEventId === e.id && 'bg-primary/15 text-primary')}
                      >
                        <span className="font-medium truncate">{e.name}</span>
                        <span className="font-mono text-muted-foreground ml-2 flex-shrink-0">{e.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {auditScope === 'incident' && (
                <div className="space-y-2">
                  <input
                    value={scopeSearch}
                    onChange={(e) => setScopeSearch(e.target.value)}
                    placeholder="Search incidents by title or ID..."
                    className="w-full px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-border bg-card divide-y divide-border">
                    {filteredIncidents.length === 0 && <p className="p-2 text-xs text-muted-foreground text-center">No incidents match.</p>}
                    {filteredIncidents.map(i => (
                      <button
                        key={i.id}
                        onClick={() => setAuditSelectedIncidentId(i.id)}
                        className={cn('w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors flex justify-between',
                          auditSelectedIncidentId === i.id && 'bg-primary/15 text-primary')}
                      >
                        <span className="font-medium truncate">{i.title}</span>
                        <span className="font-mono text-muted-foreground ml-2 flex-shrink-0">{i.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Secondary filters */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <input
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                  placeholder="Search action/details/user..."
                  className="px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <select
                  value={auditCategory}
                  onChange={(e) => setAuditCategory(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">All Categories</option>
                  <option value="auth">Auth</option>
                  <option value="user">User</option>
                  <option value="event">Event</option>
                  <option value="incident">Incident</option>
                  <option value="dispatch">Dispatch</option>
                  <option value="mdt">MDT</option>
                  <option value="resource">Resource</option>
                  <option value="prf">PRF</option>
                  <option value="config">Config</option>
                  <option value="audit">Audit</option>
                  <option value="system">System</option>
                </select>
                <input
                  type="datetime-local"
                  value={auditFrom}
                  onChange={(e) => setAuditFrom(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  title="From"
                />
                <input
                  type="datetime-local"
                  value={auditTo}
                  onChange={(e) => setAuditTo(e.target.value)}
                  className="px-3 py-2 rounded-lg bg-card border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  title="To"
                />
              </div>
            </div>

            {/* Results */}
            <div className="glass-panel">
              <div className="p-3 border-b border-border flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground uppercase tracking-wider">
                  {auditScope === 'all' ? 'All Activity' :
                    auditScope === 'event' && auditSelectedEventId ? `Event: ${auditEvents.find(e => e.id === auditSelectedEventId)?.name || auditSelectedEventId}` :
                    auditScope === 'incident' && auditSelectedIncidentId ? `Incident: ${auditIncidents.find(i => i.id === auditSelectedIncidentId)?.title || auditSelectedIncidentId}` :
                    'Select a record above'}
                </h2>
                {auditLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
              </div>

              {auditError && (
                <div className="p-3 bg-destructive/10 border-b border-destructive/30 flex items-center gap-2 text-destructive text-xs">
                  <AlertTriangle className="w-4 h-4" /> {auditError}
                </div>
              )}

              <div className="divide-y divide-border max-h-[calc(100vh-360px)] overflow-y-auto">
                {auditLogs.length === 0 && !auditLoading && (
                  <p className="p-6 text-center text-sm text-muted-foreground">No audit log entries match the current filters.</p>
                )}
                {auditLogs.map((log) => {
                  const ts = new Date(log.timestamp);
                  const expanded = expandedAuditId === log.id;
                  const catColor = categoryColors[log.category] || categoryColors.system;
                  return (
                    <div key={log.id} className="hover:bg-accent/30 transition-colors">
                      <button
                        onClick={() => setExpandedAuditId(expanded ? null : log.id)}
                        className="w-full text-left flex items-start gap-3 p-3"
                      >
                        <div className="flex flex-col items-start w-32 flex-shrink-0">
                          <span className="text-xs font-mono text-foreground">{ts.toLocaleString('en-GB', { hour12: false })}</span>
                          <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded border mt-1', catColor)}>{log.category.toUpperCase()}</span>
                        </div>
                        <div className="flex flex-col w-44 flex-shrink-0">
                          <span className="text-sm text-foreground font-medium truncate">{log.userName}</span>
                          {log.userRole && <span className="text-[10px] text-muted-foreground font-mono truncate">{log.userRole}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground font-medium truncate">{log.action}</p>
                          <p className="text-xs text-muted-foreground truncate">{log.details}</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {log.eventId && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{log.eventId}</span>}
                            {log.incidentId && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">{log.incidentId}</span>}
                            {log.resourceId && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">{log.resourceId}</span>}
                          </div>
                        </div>
                        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />}
                      </button>
                      {expanded && (
                        <div className="px-3 pb-3 pl-[148px] space-y-1.5 text-xs">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            <div><span className="text-muted-foreground">Log ID:</span> <span className="font-mono text-foreground">{log.id}</span></div>
                            <div><span className="text-muted-foreground">Timestamp:</span> <span className="font-mono text-foreground">{log.timestamp}</span></div>
                            <div><span className="text-muted-foreground">User ID:</span> <span className="font-mono text-foreground">{log.userId}</span></div>
                            <div><span className="text-muted-foreground">IP Address:</span> <span className="font-mono text-foreground">{log.ipAddress || '—'}</span></div>
                          </div>
                          {log.userAgent && (
                            <div className="pt-1 border-t border-border">
                              <span className="text-muted-foreground">User Agent:</span> <span className="font-mono text-foreground break-all">{log.userAgent}</span>
                            </div>
                          )}
                          {log.metadata && (
                            <div className="pt-1 border-t border-border">
                              <p className="text-muted-foreground mb-1">Metadata:</p>
                              <pre className="bg-card border border-border rounded p-2 text-[11px] font-mono text-foreground overflow-x-auto">{JSON.stringify(log.metadata, null, 2)}</pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}
      </div>
    </div>
  );
}
